import { Box, Typography } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceDot,
  Label,
} from "recharts";
import { ListDivider } from "../../../ui/ListStyles";
import { ChartTooltip, COLORS } from "../../../ui/Theme";
import { useMemo } from "react";

const CustomTooltip = ({ active, payload, y_1_title, y_2_title }) => {
  if (active && payload && payload.length) {
    const { x, y_1, y_2 } = payload[0].payload;
    return (
      <ChartTooltip sx={{ bgcolor: COLORS.SOFTGRAY }}>
        <Typography sx={{ fontSize: "12px", fontWeight: 900 }}>{x}</Typography>
        <ListDivider />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: COLORS.BLUE,
            }}
          >
            <Typography sx={{ fontSize: "12px" }}>{y_1_title} :</Typography>
            <Typography
              sx={{ fontSize: "12px", fontWeight: 900, direction: "rtl" }}
            >
              {y_1.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: COLORS.ORANGE,
            }}
          >
            <Typography sx={{ fontSize: "12px" }}>{y_2_title} :</Typography>
            <Typography
              sx={{ fontSize: "12px", fontWeight: 900, direction: "rtl" }}
            >
              {y_2.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </ChartTooltip>
    );
  }
  return null;
};

const Chart = ({ chartData }) => {
  const { chart_title, history, y_1_title, y_2_title } = chartData;

  const y1Values = useMemo(() => history.map((item) => item.y_1), [history]);
  const y2Values = useMemo(() => history.map((item) => item.y_2), [history]);

  const minY = Math.min(...y1Values, ...y2Values);
  const maxY = Math.max(...y1Values, ...y2Values);
  const padding = (maxY - minY) * 0.2;

  const latestData = history[history.length - 1];

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart
        data={history}
        margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
      >
        <defs>
          <linearGradient id="colorBlue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={COLORS.BLUE} stopOpacity={0.6} />
            <stop offset="95%" stopColor={COLORS.BLUE} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorOrange" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={COLORS.ORANGE} stopOpacity={0.6} />
            <stop offset="95%" stopColor={COLORS.ORANGE} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="x"
          tick={false}
          axisLine={false}
          height={0}
          padding={{ left: 0, right: 0 }}
        />
        <YAxis
          width={0}
          domain={[minY - padding, maxY + padding]}
          axisLine={false}
          tick={false}
        />
        <Legend
          wrapperStyle={{
            width: "fit-content",
            top: -10,
            right: 0,
            lineHeight: "20px",
          }}
          verticalAlign="top"
          formatter={(value) => {
            if (value === "y_1")
              return (
                <span style={{ color: COLORS.BLUE, marginRight: "5px" }}>
                  {y_1_title}
                </span>
              );
            if (value === "y_2")
              return (
                <span style={{ color: COLORS.ORANGE, marginRight: "5px" }}>
                  {y_2_title}
                </span>
              );
            return value;
          }}
        />
        <Tooltip
          cursor={{ fill: COLORS.SOFTGRAY }}
          content={
            <CustomTooltip y_1_title={y_1_title} y_2_title={y_2_title} />
          }
        />
        <Area
          type="monotone"
          dataKey="y_1"
          stroke={COLORS.BLUE}
          strokeWidth={2}
          fill="url(#colorBlue)"
          dot={false}
        />
        <Area
          type="monotone"
          dataKey="y_2"
          stroke={COLORS.ORANGE}
          strokeWidth={2}
          fill="url(#colorOrange)"
          dot={false}
        />
        <ReferenceDot
          x={latestData.x}
          y={latestData.y_1}
          r={5}
          fill={COLORS.BLUE}
          stroke="#FFFFFF"
          strokeWidth={1}
        >
          <Label
            style={{ unicodeBidi: "plaintext" }}
            value={latestData.y_1.toLocaleString()}
            position="insideBottomLeft"
            fill={COLORS.BLUE}
            fontSize={12}
            fontWeight={700}
            offset={10}
          />
        </ReferenceDot>
        <ReferenceDot
          x={latestData.x}
          y={latestData.y_2}
          r={5}
          fill={COLORS.ORANGE}
          stroke="#FFFFFF"
          strokeWidth={1}
        >
          <Label
            style={{ unicodeBidi: "plaintext" }}
            value={latestData.y_2.toLocaleString()}
            position="insideBottomLeft"
            fill={COLORS.ORANGE}
            fontSize={12}
            fontWeight={700}
            offset={10}
          />
        </ReferenceDot>
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default Chart;
